// ============================================
// Chat Status Store (Zustand)
// 채팅 대기 상태 및 읽지 않은 메시지 관리
// ============================================

import { createStore } from 'zustand/vanilla';
import { persist } from 'zustand/middleware';

const PENDING_TIMEOUT = 2 * 60 * 1000; // 2분

export const useChatStatusStore = createStore(
  persist(
    (set, get) => ({
      // ============================================
      // State
      // ============================================

      // 대기 중인 채팅 { chatId: { userInput, startedAt, status } }
      pendingChats: {},

      // 읽지 않은 메시지가 있는 채팅 { chatId: true }
      unreadChats: {},

      // 마지막 메시지 캐시 { chatId: messageContent }
      lastMessages: {},

      // ============================================
      // Actions - Pending
      // ============================================

      setPending: (chatId, data) => set(state => ({
        pendingChats: {
          ...state.pendingChats,
          [chatId]: {
            userInput: data.userInput,
            startedAt: data.startedAt || Date.now(),
            status: data.status || 'streaming'
          }
        }
      })),

      clearPending: (chatId) => set(state => {
        const { [chatId]: _, ...rest } = state.pendingChats;
        return { pendingChats: rest };
      }),

      // ============================================
      // Actions - Unread
      // ============================================

      setUnread: (chatId) => set(state => ({
        unreadChats: { ...state.unreadChats, [chatId]: true }
      })),

      clearUnread: (chatId) => set(state => {
        const { [chatId]: _, ...rest } = state.unreadChats;
        return { unreadChats: rest };
      }),

      markAsRead: (chatId) => set(state => {
        const { [chatId]: _, ...rest } = state.unreadChats;
        return { unreadChats: rest };
      }),

      // ============================================
      // Actions - Last Message
      // ============================================

      setLastMessage: (chatId, message) => set(state => ({
        lastMessages: { ...state.lastMessages, [chatId]: message }
      })),

      clearLastMessage: (chatId) => set(state => {
        const { [chatId]: _, ...rest } = state.lastMessages;
        return { lastMessages: rest };
      }),

      getLastMessage: (chatId) => get().lastMessages[chatId] || null,

      // ============================================
      // Actions - Cleanup
      // ============================================

      cleanupStalePending: () => {
        const { pendingChats } = get();
        const now = Date.now();
        const staleChatIds = [];

        Object.entries(pendingChats).forEach(([chatId, data]) => {
          if (now - data.startedAt > PENDING_TIMEOUT) {
            staleChatIds.push(chatId);
          }
        });

        if (staleChatIds.length > 0) {
          set(state => {
            const newPending = { ...state.pendingChats };
            staleChatIds.forEach(id => delete newPending[id]);
            return { pendingChats: newPending };
          });
        }

        return staleChatIds;
      },

      // ============================================
      // Selectors
      // ============================================

      isPending: (chatId) => !!get().pendingChats[chatId],

      getPending: (chatId) => get().pendingChats[chatId] || null,

      isUnread: (chatId) => !!get().unreadChats[chatId],

      getTotalUnreadCount: () => Object.keys(get().unreadChats).length,

      // 타임아웃 여부 확인
      isTimedOut: (chatId) => {
        const pending = get().pendingChats[chatId];
        if (!pending) return false;
        return Date.now() - pending.startedAt > PENDING_TIMEOUT;
      }
    }),
    {
      name: 'chatai-chat-status',
      partialize: (state) => ({
        pendingChats: state.pendingChats,
        unreadChats: state.unreadChats
      })
    }
  )
);

export default useChatStatusStore;
